import React, {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Stack, Button, Text, Link} from '@salesforce/retail-react-app/app/components/shared/ui'
import * as api from '@purple-dot/browser/api'
import {purpleDotCheckout} from '@purple-dot/browser/checkout'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {KS_PERSONAL_PRIVACY_POLICY_URL, KS_PERSONAL_TERMS_CONDITIONS_URL} from '../../constants'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useTagManager from '../../hooks/use-tag-manager'

const PDButton = ({product, variant, currency}) => {
    const tagManager = useTagManager()
    const id = product?.id || variant?.variantId
    const [variantPurpleDotState, setVariantPurpleDotState] = useState({})
    const {
        app: {commerceAPI}
    } = getConfig()
    const {data: customer} = useCurrentCustomer()
    useEffect(() => {
        window.globalData = {
            userID: customer?.customerNo || ''
        }
    }, [customer?.customerNo])

    const {hostUrl} = commerceAPI.storefront
    const [showSpinner, setShowSpinner] = useState(false)
    const [showAdded, setShowAdded] = useState(false)
    const onPreOrderClick = useCallback(() => {
        purpleDotCheckout(async (element) => {
            if (!variantPurpleDotState?.waitlist?.id) {
                return
            }
            setTimeout(() => {
                setShowSpinner(true)
            }, 100)
            try {
                const res = await element.addItem({
                    variantId: id,
                    releaseId: variantPurpleDotState.waitlist.id,
                    currency: currency,
                    quantity: 1
                })
                if (res.success) {
                    element.show()
                    setShowAdded(true)
                }
                setTimeout(() => {
                    setShowAdded(false)
                }, 3000)
            } catch (e) {
                console.error(e)
            }
            setShowSpinner(false)
        })
    }, [variant, variantPurpleDotState?.waitlist?.id])

    useEffect(() => {
        async function fetchData() {
            const variantResponse = await api.fetchVariantsPreorderState(id)
            setVariantPurpleDotState(variantResponse)
        }

        if (id) {
            fetchData()
        }
    }, [id])

    let purpleDotListenersAttached = false

    useEffect(() => {
        if (purpleDotListenersAttached) return
        purpleDotListenersAttached = true

        const handleAddToCart = () => {
            document.querySelector('.pd-icon').classList.remove('hide-element')
            tagManager.addToCart(product, variant, null, null, 'pdp', null, true)
            tagManager.addToCartGA4({
                itemsAdded: [{product: product?.representedProduct || product}],
                variant,
                method: 'pre-order',
                currency: currency
            })
        }

        window.addEventListener('PurpleDot:AddToCart', handleAddToCart)

        return () => {
            window.removeEventListener('PurpleDot:AddToCart', handleAddToCart)
            purpleDotListenersAttached = false
        }
    }, [])

    return (
        <Stack width="full" gap="30px" marginBottom="30px">
            <Text
                color="black.100"
                fontSize="13px"
                fontWeight="300"
                lineHeight="22px"
                letterSpacing="0.065px"
            >
                <FormattedMessage
                    id="purple_dot.by_clicking_pre_order_you_agree_to_kendra_scotts"
                    defaultMessage="By clicking Pre-Order, you agree to Kendra Scott’s"
                />
                <br />
                <Link href={hostUrl + KS_PERSONAL_TERMS_CONDITIONS_URL} variant="underline">
                    <FormattedMessage
                        id="purple_dot.terms_conditions"
                        defaultMessage="Terms & Conditions"
                    />
                </Link>{' '}
                <FormattedMessage id="purple_dot.and" defaultMessage="and" />{' '}
                <Link href={hostUrl + KS_PERSONAL_PRIVACY_POLICY_URL} variant="underline">
                    <FormattedMessage
                        id="purple_dot.privacy_policy"
                        defaultMessage="Privacy Policy"
                    />
                </Link>
                {'.'}
            </Text>

            <Button
                onClick={onPreOrderClick}
                isLoading={showSpinner}
                width="full"
                height="56px"
                backgroundColor="purple.700"
                fontSize="17px"
                fontWeight="400"
                lineHeight="26px"
                letterSpacing="0.085px"
                _hover={{
                    backgroundColor: 'purple.100'
                }}
            >
                {showAdded ? (
                    <FormattedMessage id="added.to.cart" defaultMessage="Added" />
                ) : (
                    <FormattedMessage id="purple_dot.pre_order" defaultMessage="Pre-Order" />
                )}
            </Button>

            <Stack
                gap="5px"
                color="black.100"
                fontSize="14px"
                fontWeight="300"
                lineHeight="22px"
                letterSpacing="0.07px"
            >
                {variantPurpleDotState?.waitlist?.display_dispatch_date && (
                    <Text>{variantPurpleDotState.waitlist.display_dispatch_date}</Text>
                )}
                <purple-dot-learn-more />
            </Stack>
        </Stack>
    )
}

PDButton.propTypes = {
    variant: PropTypes.object,
    product: PropTypes.object,
    currency: PropTypes.string
}

export default PDButton
